import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { FileReviewAnchor, FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import type {
  AutoSaveBridgeStatus,
  SpreadsheetBridgeMessage,
  SpreadsheetHostMessage,
  SpreadsheetNavigateAnchorPayload,
  SpreadsheetSelectionState
} from './spreadsheetBridgeTypes';
import {
  readEditorAutoSavePrefs,
  EDITOR_AUTOSAVE_PREFS_CHANGED
} from '../editorPreferences/editorAutoSavePrefs';
import {
  normalizeSpreadsheetRanges,
  spreadsheetAnchorBookmark
} from './spreadsheetBridgeTypes';
import type { ForceReloadOptions } from '../realtime/documentCollaborationTypes';

function isBridgeMessage(data: unknown): data is SpreadsheetBridgeMessage {
  return Boolean(data && typeof data === 'object' && 'type' in data);
}

export interface AutoSaveBridgeState {
  status: AutoSaveBridgeStatus;
  lastSavedAt: number | null;
  reason: string | null;
}

const DIRTY_STATE_TIMEOUT_MS = 1500;
const SAVE_REQUEST_TIMEOUT_MS = 70000;

export interface AutoSaveRequestResult {
  ok: boolean;
  reason: string | null;
}

export interface UseSpreadsheetHostBridgeOptions {
  onNavigationFailed?: (rangeLabel?: string) => void;
  onOpenFailed?: (message?: string | null) => void;
  onRequestCommentOnSelection?: (selection?: SpreadsheetSelectionState | null) => void;
  onRequestToggleCellLinks?: () => void;
  onRequestToggleDiscussion?: () => void;
  onRequestShare?: () => void;
  onRequestOpen?: (source: 'pc' | 'storage') => void;
  onRequestSetDiscussionPlacement?: (placement: FileReviewPanelPlacement) => void;
}

export function useSpreadsheetHostBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  options?: UseSpreadsheetHostBridgeOptions
) {
  const [ready, setReady] = useState(false);
  const [editorSessionId, setEditorSessionId] = useState<string | null>(null);
  const [selection, setSelection] = useState<SpreadsheetSelectionState | null>(null);
  const [autoSave, setAutoSave] = useState<AutoSaveBridgeState>({
    status: 'idle',
    lastSavedAt: null,
    reason: null
  });
  const dirtyResolverRef = useRef<{ resolve: (dirty: boolean) => void; timer: number } | null>(null);
  const saveResolverRef = useRef<{
    requestId: string;
    resolve: (result: AutoSaveRequestResult) => void;
    timer: number;
  } | null>(null);
  const onNavigationFailedRef = useRef(options?.onNavigationFailed);
  const onOpenFailedRef = useRef(options?.onOpenFailed);
  const onRequestCommentRef = useRef(options?.onRequestCommentOnSelection);
  const onRequestToggleCellLinksRef = useRef(options?.onRequestToggleCellLinks);
  const onRequestToggleDiscussionRef = useRef(options?.onRequestToggleDiscussion);
  const onRequestShareRef = useRef(options?.onRequestShare);
  const onRequestOpenRef = useRef(options?.onRequestOpen);
  const onRequestSetDiscussionPlacementRef = useRef(options?.onRequestSetDiscussionPlacement);
  const iframeOriginRef = useRef<string | null>(null);
  onNavigationFailedRef.current = options?.onNavigationFailed;
  onOpenFailedRef.current = options?.onOpenFailed;
  onRequestCommentRef.current = options?.onRequestCommentOnSelection;
  onRequestToggleCellLinksRef.current = options?.onRequestToggleCellLinks;
  onRequestToggleDiscussionRef.current = options?.onRequestToggleDiscussion;
  onRequestShareRef.current = options?.onRequestShare;
  onRequestOpenRef.current = options?.onRequestOpen;
  onRequestSetDiscussionPlacementRef.current = options?.onRequestSetDiscussionPlacement;

  const postToHost = useCallback(
    (message: SpreadsheetHostMessage) => {
      const iframe = iframeRef.current;
      const win = iframe?.contentWindow;
      if (!win) return;
      let targetOrigin = window.location.origin;
      try {
        if (iframe.src) {
          targetOrigin = new URL(iframe.src, window.location.href).origin;
        }
      } catch {
        /* keep parent origin */
      }
      win.postMessage(message, targetOrigin);
    },
    [iframeRef]
  );

  const setDiscussionActive = useCallback(
    (active: boolean) => {
      postToHost({ type: 'jarvis-spreadsheet:set-discussion-active', active });
    },
    [postToHost]
  );

  const setCellLinksState = useCallback(
    (on: boolean) => {
      postToHost({ type: 'jarvis-spreadsheet:cell-links-state', on });
    },
    [postToHost]
  );

  const setDiscussionState = useCallback(
    (on: boolean) => {
      postToHost({ type: 'jarvis-spreadsheet:discussion-state', on });
    },
    [postToHost]
  );

  const setDiscussionPlacementState = useCallback(
    (placement: FileReviewPanelPlacement) => {
      postToHost({ type: 'jarvis-spreadsheet:discussion-placement-state', placement });
    },
    [postToHost]
  );

  const cancelNavigation = useCallback(() => {
    postToHost({ type: 'jarvis-spreadsheet:cancel-navigation' });
  }, [postToHost]);

  const requestAutoSaveNow = useCallback((): Promise<AutoSaveRequestResult> => {
    const prior = saveResolverRef.current;
    if (prior) {
      window.clearTimeout(prior.timer);
      prior.resolve({ ok: false, reason: 'superseded' });
      saveResolverRef.current = null;
    }
    const requestId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise<AutoSaveRequestResult>((resolve) => {
      const timer = window.setTimeout(() => {
        if (saveResolverRef.current?.requestId === requestId) saveResolverRef.current = null;
        resolve({ ok: false, reason: 'timeout' });
      }, SAVE_REQUEST_TIMEOUT_MS);
      saveResolverRef.current = { requestId, resolve, timer };
      postToHost({ type: 'jarvis-spreadsheet:request-auto-save-now', requestId });
    });
  }, [postToHost]);

  const setPeerEditLocked = useCallback(
    (locked: boolean) => {
      postToHost({ type: 'jarvis-spreadsheet:set-peer-edit-locked', locked });
    },
    [postToHost]
  );

  const requestForceReload = useCallback((options?: ForceReloadOptions) => {
    postToHost({
      type: 'jarvis-spreadsheet:request-force-reload',
      overlay: options?.overlay === true
    });
  }, [postToHost]);

  /** Updates the title-bar indicator when a save completes (local or remote via SignalR). */
  const applyCollaborationSaveTime = useCallback((savedAtUtc: string) => {
    const ms = Date.parse(savedAtUtc);
    const lastSavedAt = Number.isFinite(ms) ? ms : Date.now();
    setAutoSave({ status: 'saved', lastSavedAt, reason: null });
  }, []);

  /** Asks the iframe whether it has unsaved local edits. Resolves dirty=true on timeout (safer). */
  const requestDirtyState = useCallback((): Promise<boolean> => {
    const prior = dirtyResolverRef.current;
    if (prior) {
      window.clearTimeout(prior.timer);
      prior.resolve(true);
      dirtyResolverRef.current = null;
    }
    return new Promise<boolean>((resolve) => {
      const timer = window.setTimeout(() => {
        dirtyResolverRef.current = null;
        resolve(true);
      }, DIRTY_STATE_TIMEOUT_MS);
      dirtyResolverRef.current = { resolve, timer };
      postToHost({ type: 'jarvis-spreadsheet:request-dirty-state' });
    });
  }, [postToHost]);

  const navigateToAnchor = useCallback(
    (anchor: FileReviewAnchor) => {
      if (anchor.kind !== 'spreadsheet') return;
      const ranges = normalizeSpreadsheetRanges(anchor.ranges);
      const bookmark = spreadsheetAnchorBookmark(anchor);
      if (!ranges.length && !bookmark) return;
      const payload: SpreadsheetNavigateAnchorPayload = {
        sheetIndex: anchor.sheetIndex ?? 0,
        sheetName: anchor.sheetName ?? undefined,
        bookmark: bookmark ?? undefined,
        ranges,
        anchorId: anchor.id
      };
      postToHost({ type: 'jarvis-spreadsheet:navigate-anchor', payload });
    },
    [postToHost]
  );

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setEditorSessionId(null);
      setSelection(null);
    }
  }, [enabled]);

  useEffect(
    () => () => {
      const dirty = dirtyResolverRef.current;
      if (dirty) {
        window.clearTimeout(dirty.timer);
        dirty.resolve(true);
        dirtyResolverRef.current = null;
      }
      const save = saveResolverRef.current;
      if (save) {
        window.clearTimeout(save.timer);
        save.resolve({ ok: false, reason: 'unmounted' });
        saveResolverRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;

    function onMessage(event: MessageEvent) {
      const iframeOrigin = iframeOriginRef.current;
      const originOk =
        event.origin === window.location.origin ||
        (iframeOrigin != null && event.origin === iframeOrigin);
      const fromOurIframe = event.source === iframeRef.current?.contentWindow;
      if (fromOurIframe && isBridgeMessage(event.data) && !originOk) {
        console.warn(
          '[jarvis-spreadsheet] dropped message — origin mismatch',
          event.origin,
          'expected',
          window.location.origin,
          'or',
          iframeOrigin
        );
        return;
      }
      if (!originOk) return;
      if (!fromOurIframe) return;
      if (!isBridgeMessage(event.data)) return;

      switch (event.data.type) {
        case 'jarvis-spreadsheet:ready':
          if (event.data.hostOrigin) iframeOriginRef.current = event.data.hostOrigin;
          setEditorSessionId(event.data.sessionId?.trim() || null);
          setReady(true);
          break;
        case 'jarvis-spreadsheet:open-failed':
          onOpenFailedRef.current?.(event.data.message ?? null);
          break;
        case 'jarvis-spreadsheet:selection-changed':
          setSelection(event.data.selection);
          break;
        case 'jarvis-spreadsheet:navigation-complete':
          if (!event.data.ok) {
            onNavigationFailedRef.current?.(event.data.rangeLabel);
          }
          break;
        case 'jarvis-spreadsheet:request-comment-on-selection':
          if (onRequestCommentRef.current) {
            onRequestCommentRef.current(event.data.selection ?? null);
          } else {
            console.warn('[jarvis-spreadsheet] no handler for request-comment-on-selection');
          }
          break;
        case 'jarvis-spreadsheet:request-toggle-cell-links':
          if (onRequestToggleCellLinksRef.current) onRequestToggleCellLinksRef.current();
          else console.warn('[jarvis-spreadsheet] no handler for request-toggle-cell-links');
          break;
        case 'jarvis-spreadsheet:request-toggle-discussion':
          if (onRequestToggleDiscussionRef.current) onRequestToggleDiscussionRef.current();
          else console.warn('[jarvis-spreadsheet] no handler for request-toggle-discussion');
          break;
        case 'jarvis-spreadsheet:request-share':
          onRequestShareRef.current?.();
          break;
        case 'jarvis-spreadsheet:request-open': {
          const source = event.data.source === 'storage' ? 'storage' : 'pc';
          onRequestOpenRef.current?.(source);
          break;
        }
        case 'jarvis-spreadsheet:request-set-discussion-placement':
          onRequestSetDiscussionPlacementRef.current?.(event.data.placement);
          break;
        case 'jarvis-spreadsheet:dirty-state': {
          const pending = dirtyResolverRef.current;
          if (pending) {
            window.clearTimeout(pending.timer);
            dirtyResolverRef.current = null;
            pending.resolve(Boolean(event.data.dirty));
          }
          break;
        }
        case 'jarvis-spreadsheet:auto-save-state':
          setAutoSave({
            status: event.data.status,
            lastSavedAt: event.data.lastSavedAtUtc ?? null,
            reason: event.data.reason ?? null
          });
          break;
        case 'jarvis-spreadsheet:auto-save-complete': {
          const pending = saveResolverRef.current;
          if (pending && pending.requestId === event.data.requestId) {
            window.clearTimeout(pending.timer);
            saveResolverRef.current = null;
            pending.resolve({
              ok: Boolean(event.data.ok),
              reason: event.data.reason ?? null
            });
          }
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [enabled, iframeRef]);

  const pushAutoSaveConfig = useCallback(() => {
    const prefs = readEditorAutoSavePrefs();
    postToHost({
      type: 'jarvis-spreadsheet:set-autosave-config',
      enabled: prefs.enabled,
      intervalSeconds: prefs.intervalSeconds
    });
  }, [postToHost]);

  useEffect(() => {
    if (!enabled || !ready) return;
    pushAutoSaveConfig();
  }, [enabled, ready, pushAutoSaveConfig]);

  useEffect(() => {
    if (!enabled || !ready) return;
    const onPrefsChanged = () => pushAutoSaveConfig();
    window.addEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onPrefsChanged);
    return () => window.removeEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onPrefsChanged);
  }, [enabled, ready, pushAutoSaveConfig]);

  return {
    ready,
    editorSessionId,
    selection,
    autoSave,
    navigateToAnchor,
    cancelNavigation,
    requestAutoSaveNow,
    requestForceReload,
    applyCollaborationSaveTime,
    requestDirtyState,
    postToHost,
    setDiscussionActive,
    setCellLinksState,
    setDiscussionState,
    setDiscussionPlacementState,
    setPeerEditLocked
  };
}
